import { apiClientAtom } from "@/modules/api/apiClient.store.ts";
import type { ApiClient } from "@/modules/api/apiClient.ts";
import { queryClientAtom } from "@/modules/api/queryClient.ts";
import type { QueryClient, QueryKey, QueryObserverResult } from "@tanstack/query-core";
import { QueryObserver, type UseQueryOptions, type UseSuspenseQueryOptions } from "@tanstack/react-query";
import { atom, type Atom } from "jotai/vanilla";

const enableDebugLog = false;

export function atomFromQueryOptions<TData, TError, TQueryFnData, TQueryKey extends QueryKey>(
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient: QueryClient,
): Atom<QueryObserverResult<TData, TError>> {
    let _debugId;
    function _dbg(msg: string, ...args: unknown[]) {
        if (!enableDebugLog) {
            return;
        }
        _debugId ??= JSON.stringify(defaultedOptions.queryKey);
        console.debug(`${_debugId}: ${msg}`, ...args);
    }

    // Enable optimistic results
    const defaultedOptions = queryClient.defaultQueryOptions(options);
    defaultedOptions._optimisticResults = "optimistic";

    const observer = new QueryObserver(queryClient, defaultedOptions);

    const initialResult = observer.getOptimisticResult(defaultedOptions);
    _dbg("Creating result atom with result: %o", debugFields(initialResult));

    // Only start observing the query when the atom is mounted.
    const observerStateAtom = atom(initialResult);
    observerStateAtom.onMount = (set) => {
        _dbg("Subscribing to query observer");
        const unsubscribe = observer.subscribe((state) => {
            _dbg("Query observer state change: %o", debugFields(state));
            set(state);
        });
        return () => {
            _dbg("Unsubscribing from query observer");
            unsubscribe();
        };
    };

    return atom((get) => {
        // Triggers `onMount`.
        const observedState = get(observerStateAtom);
        // We do not use the state updated by the observer (for some legacy reason?).

        const result = observer.getOptimisticResult(defaultedOptions);
        _dbg("Returning result:\nobserver: %o\noptimistic: %o)", debugFields(observedState), debugFields(result));

        return !defaultedOptions.notifyOnChangeProps
            ? // Track only accessed fields' (eg. `data`, `error`) changes.
              // Otherwise the atom is updated very frequently due to eg. changing fetch status.
              observer.trackResult(result)
            : result;
    });
}

export function atomFromStandardQueryOptions<
    TData,
    TError,
    TQueryFnData,
    TQueryKey extends QueryKey,
    TArgs extends unknown[],
>(
    getQueryOptions: (
        apiClient: ApiClient,
        ...args: TArgs
    ) => UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    ...args: TArgs
): Atom<QueryObserverResult<TData, TError>> {
    // This atom returns another atom that returns the query result. Note that we do not want to execute `get` on the
    // query atom inside here immediately, as that would lead to re-building the atom every time the actual query
    // results change.
    const wrapperAtom = atom((get) => {
        const apiClient = get(apiClientAtom);
        const options = getQueryOptions(apiClient, ...args);

        const queryClient = get(queryClientAtom);
        return atomFromQueryOptions(options, queryClient);
    });
    // Unwrap the query result from the atom-in-atom
    return atom((get) => get(get(wrapperAtom)));
}

function debugFields<TData, TError>(result: QueryObserverResult<TData, TError>) {
    if (!enableDebugLog) {
        return;
    }

    const { status, data, error } = result;
    return { status, data, error };
}
